import { useQuery } from "@apollo/client";
import _ from "lodash";
import {
  ReactElement,
  useRef,
  useMemo,
  useEffect,
  useState,
  useCallback,
} from "react";
import {
  useQueryParams,
  StringParam,
  withDefault,
  NumberParam,
} from "use-query-params";

import {
  InboxType,
  ListAssignmentsQuery,
  ListAssignmentsQueryVariables,
  GetAssignmentsQuery,
  GetAssignmentsQueryVariables,
} from "~frontend/__generated__/graphql";
import Loading from "~frontend/components/Loading";
import {
  ThreadListContainer,
  ThreadSplitView,
  ThreadListPane,
  ThreadDisplayPane,
  ThreadDetailsWrapper,
  ThreadListCollapse,
} from "~frontend/components/threads";
import {
  LIST_ASSIGNMENTS,
  GET_ASSIGNMENTS,
} from "~frontend/graphql/assignments";
import {
  Breadcrumb,
  useBreadcrumbs,
} from "~frontend/providers/BreadcrumbsProvider";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import { TexterSettingsProvider } from "~frontend/providers/TexterSettingsProvider";
import useMousetrap from "~frontend/util/useMousetrap";
import usePrevious from "~frontend/util/usePrevious";

import { AssignmentDetails } from "./AssignmentDetails";
import { AssignmentList } from "./AssignmentList";
import { InboxDetails, Assignment } from "./types";

export function InboxAssignments({
  inbox,
  campaignId,
  refetchInbox,
  headerText,
}: {
  inbox: InboxDetails;
  campaignId?: string | null;
  refetchInbox: () => Promise<any>;
  headerText?: string;
}): ReactElement {
  const { subaccountLink, subaccount } = useSubaccount()!;

  const breadcrumbs: Breadcrumb[] = [
    {
      label: subaccount.name,
      path: subaccountLink("/inboxes"),
      backArrow: true,
    },
    {
      label: inbox.inboxName,
      path: subaccountLink(`/inboxes/${inbox.id}`),
    },
  ];

  if (inbox.inboxType === InboxType.Escalation) {
    breadcrumbs.push({
      label: headerText || inbox.inboxName,
      path: subaccountLink(
        // FIXME: better route?
        `/inboxes/${inbox.id}/campaigns/${campaignId || "no_campaign"}`
      ),
    });
  }

  useBreadcrumbs(breadcrumbs, { hideSubaccountTitle: true });

  const [queryParams, setQueryParams] = useQueryParams({
    assignment: StringParam,
    page: withDefault(NumberParam, 0),
    page_size: withDefault(NumberParam, 50),
  });
  const assignmentId = queryParams.assignment;
  const page = queryParams.page;
  const pageSize = queryParams.page_size;

  // This ref lets us say that we want to auto-select the first/last thread
  // after we're done loading the next page. It's used when keyboard-navigating
  // between threads across pages.
  const autoSelectAssignmentRef = useRef<"first" | "last" | null>(null);

  const listAssignmentsResponse = useQuery<
    ListAssignmentsQuery,
    ListAssignmentsQueryVariables
  >(LIST_ASSIGNMENTS, {
    variables: {
      input: {
        inboxId: inbox.id,
        campaignId,
        limit: pageSize,
        offset: page * pageSize,
      },
    },
    pollInterval: 60 * 1000,
    fetchPolicy: "network-only",
    onCompleted: data => {
      if (autoSelectAssignmentRef.current) {
        const assignments = data.listMyAssignments.assignments;
        if (assignments.length > 0) {
          const assignment =
            assignments[
              autoSelectAssignmentRef.current === "first"
                ? 0
                : assignments.length - 1
            ];
          setQueryParams({ assignment: assignment.id }, "pushIn");
        }
        autoSelectAssignmentRef.current = null;
      }
    },
  });

  const assignmentIdx = useMemo(() => {
    const assignments =
      listAssignmentsResponse.data?.listMyAssignments.assignments;

    return (
      assignments?.findIndex(assignment => assignment.id === assignmentId) ??
      null
    );
  }, [listAssignmentsResponse, assignmentId]);
  const previousAssignmentIdx = usePrevious(assignmentIdx);
  useEffect(() => {
    const assignments =
      listAssignmentsResponse.data?.listMyAssignments.assignments;

    if (
      previousAssignmentIdx !== null &&
      assignments &&
      !assignments.find(assignment => assignment.id === assignmentId)
    ) {
      const newAssignmentId = assignments[previousAssignmentIdx]?.id ?? null;
      setQueryParams({ assignment: newAssignmentId }, "pushIn");
    }
  }, [
    listAssignmentsResponse,
    previousAssignmentIdx,
    assignmentId,
    assignmentIdx,
    setQueryParams,
  ]);

  const assignmentIdsToLoad = useMemo(() => {
    if (assignmentIdx === null) {
      return null;
    }

    const assignments =
      listAssignmentsResponse.data?.listMyAssignments.assignments;

    if (assignmentIdx !== -1) {
      return assignments
        ?.slice(
          Math.max(0, assignmentIdx - 1),
          Math.min(assignments.length, assignmentIdx + 2)
        )
        .map(assignment => assignment.id);
    } else if (assignmentId) {
      return [assignmentId];
    } else {
      return null;
    }
  }, [assignmentIdx, listAssignmentsResponse, assignmentId]);

  const getAssignmentsResponse = useQuery<
    GetAssignmentsQuery,
    GetAssignmentsQueryVariables
  >(GET_ASSIGNMENTS, {
    variables: {
      input: {
        inboxId: inbox.id,
        assignmentIds: assignmentIdsToLoad ?? [],
      },
    },
    pollInterval: 60 * 1000,
    fetchPolicy: "network-only",
    skip: !assignmentIdsToLoad,
  });
  const previousGetAssignmentsResponse = usePrevious(getAssignmentsResponse);

  const [hydratedAssignments, setHyrdratedAssignments] = useState<{
    [id: string]: Assignment;
  }>({});

  useEffect(() => {
    if (
      getAssignmentsResponse.data &&
      !_.isEqual(
        getAssignmentsResponse.data,
        previousGetAssignmentsResponse.data
      )
    ) {
      setHyrdratedAssignments({
        ...hydratedAssignments,
        ..._.keyBy(getAssignmentsResponse.data.getAssignments, "id"),
      });
    }
  }, [
    hydratedAssignments,
    getAssignmentsResponse,
    previousGetAssignmentsResponse,
  ]);

  const assignment = assignmentId ? hydratedAssignments[assignmentId] : null;

  const [loadingAssignmentIds, setLoadingAssignmentIds] = useState<string[]>(
    []
  );
  const markAssignmentLoading = (assignmentId: string) =>
    setLoadingAssignmentIds([
      ...new Set([...loadingAssignmentIds, assignmentId]),
    ]);
  const markAssignmentLoaded = (assignmentId: string) =>
    setLoadingAssignmentIds(
      loadingAssignmentIds.filter(id => id !== assignmentId)
    );

  const refetchAssignment = useCallback(async () => {
    await Promise.all([refetchInbox(), getAssignmentsResponse.refetch()]);
  }, [refetchInbox, getAssignmentsResponse]);

  function handleNavShortcut(direction: 1 | -1) {
    if (!listAssignmentsResponse.data) {
      return;
    }

    const assignments =
      listAssignmentsResponse.data.listMyAssignments.assignments;

    const currentIdx = assignments.findIndex(a => a.id === assignmentId);
    if (currentIdx === -1) {
      return;
    }

    const newIdx = currentIdx + direction;
    if (newIdx < 0) {
      if (page > 0) {
        autoSelectAssignmentRef.current = "last";
        setQueryParams(
          {
            page: page - 1,
          },
          "pushIn"
        );
      }
    } else if (newIdx >= assignments.length) {
      const totalCount =
        listAssignmentsResponse.data.listMyAssignments.totalCount;
      if (totalCount > (page + 1) * pageSize) {
        autoSelectAssignmentRef.current = "first";
        setQueryParams(
          {
            page: page + 1,
          },
          "pushIn"
        );
      }
    } else {
      const newAssignment = assignments[newIdx];
      setQueryParams(
        {
          assignment: newAssignment.id,
        },
        "pushIn"
      );
    }
  }

  useMousetrap("shift+up", () => {
    handleNavShortcut(-1);
  });

  useMousetrap("shift+down", () => {
    handleNavShortcut(1);
  });

  return (
    <TexterSettingsProvider>
      <ThreadListContainer>
        <ThreadSplitView>
          <ThreadListCollapse>
            <ThreadListPane>
              {listAssignmentsResponse.data ? (
                <AssignmentList
                  inbox={inbox}
                  headerText={headerText}
                  assignments={
                    listAssignmentsResponse.data.listMyAssignments.assignments
                  }
                  totalCount={
                    listAssignmentsResponse.data.listMyAssignments.totalCount
                  }
                  page={page}
                  pageSize={pageSize}
                  onChangePage={newPage =>
                    setQueryParams({
                      ...queryParams,
                      page: newPage,
                    })
                  }
                  onChangePageSize={newPageSize =>
                    setQueryParams({
                      ...queryParams,
                      page_size: newPageSize,
                    })
                  }
                  selectedAssignmentId={assignmentId ?? null}
                  onChangeSelectedAssignmentId={id =>
                    setQueryParams({ assignment: id })
                  }
                  loadingAssignmentIds={loadingAssignmentIds}
                />
              ) : (
                <Loading />
              )}
            </ThreadListPane>
          </ThreadListCollapse>
          <ThreadDisplayPane>
            <ThreadDetailsWrapper>
              {assignment ? (
                <AssignmentDetails
                  assignment={assignment}
                  inboxType={inbox.inboxType}
                  inboxName={inbox.inboxName}
                  refetchAssignment={refetchAssignment}
                  markAssignmentLoading={() =>
                    markAssignmentLoading(assignment.id)
                  }
                  markAssignmentLoaded={() =>
                    markAssignmentLoaded(assignment.id)
                  }
                  advanceToNextAssignment={() => handleNavShortcut(1)}
                />
              ) : null}
            </ThreadDetailsWrapper>
          </ThreadDisplayPane>
        </ThreadSplitView>
      </ThreadListContainer>
    </TexterSettingsProvider>
  );
}
